import { createExpressionRuleProgram } from "@crown/dsl";
import { z } from "zod";
const WRONG_RESULT = 'It parses and runs, but its results do not match what was asked.';
const MOST_REASONS = 6;
const LONGEST_REASON = 400;
function diagnosticReasons(diagnostics) {
    const first = diagnostics.find((d)=>'error' === d.severity);
    if (!first) return [];
    const reasons = [
        first.code ? `${first.code}: ${first.message}` : first.message
    ];
    if (first.help) reasons.push(first.help);
    for (const s of first.suggestions ?? [])reasons.push(`try ${s.replacement}: ${s.message}`);
    return reasons;
}
function feedbackTurn(answer, reasons) {
    const said = reasons.map((reason)=>[
            ...reason.trim()
        ].slice(0, LONGEST_REASON).join('')).filter((reason)=>reason.length > 0).slice(0, MOST_REASONS);
    const listed = (said.length > 0 ? said : [
        WRONG_RESULT
    ]).map((reason)=>`- ${reason}`).join('\n');
    return `Your answer was:\n${answer}\n\nThe checker refused it:\n${listed}\n\nWrite the corrected answer.`;
}
const Form = z.string();
function wrapDslAsRule(dsl) {
    return {
        type: 'expr',
        expression: dsl.trim()
    };
}
const RuleTargetSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('expr'),
        expression: z.string()
    }),
    z.object({
        type: z.literal('dsl'),
        dsl: z.string()
    }),
    z.object({
        type: z.literal('natural'),
        natural: z.string()
    })
]);
function parseRuleTarget(s) {
    let parsed;
    try {
        parsed = JSON.parse(s);
    } catch  {
        return null;
    }
    const target = RuleTargetSchema.safeParse(parsed);
    return target.success ? target.data : null;
}
const DSL_SYSTEM = "You convert a natural-language requirement into ONE Crown DSL expression — the expression language used across rule conditions, computed fields, mapper transforms and controls. Output ONLY the expression: no JSON, no quotes, no prose, no code fences.\nRefer to fields as root.<field>, the value under evaluation as value, and context as ctx.<key>.";
function isValidDsl(dsl) {
    const trimmed = dsl.trim();
    if (null !== parseRuleTarget(trimmed)) return false;
    const prog = createExpressionRuleProgram(trimmed);
    const snap = prog.getSnapshot();
    return snap.isValid && snap.diagnostics.every((d)=>'error' !== d.severity);
}
function dslDiagnostics(dsl) {
    const snap = createExpressionRuleProgram(dsl.trim()).getSnapshot();
    const errors = snap.diagnostics.filter((d)=>'error' === d.severity).map((d)=>d.message);
    if (snap.isValid && 0 === errors.length) return null;
    return errors.length ? errors.join('; ') : 'expression failed validation';
}
function dslReasons(dsl) {
    const snap = createExpressionRuleProgram(dsl.trim()).getSnapshot();
    const errors = snap.diagnostics.filter((d)=>'error' === d.severity);
    if (snap.isValid && 0 === errors.length) return null;
    const reasons = diagnosticReasons(snap.diagnostics);
    return reasons.length > 0 ? reasons : [
        'parse-error'
    ];
}
function extractDsl(completion) {
    let t = completion.trim().replace(/^```[a-z]*\n?/i, '').replace(/```\s*$/, '').trim();
    if (t.startsWith('{')) {
        const parsed = parseRuleTarget(t);
        if (parsed) t = 'expr' === parsed.type ? parsed.expression : 'dsl' === parsed.type ? parsed.dsl : parsed.natural;
    }
    t = t.trim();
    return t.length ? t : null;
}
const crownDslNano = {
    id: 'crown-dsl',
    onnxFolder: 'crown-dsl',
    system: DSL_SYSTEM,
    extractTarget: (completion)=>extractDsl(completion),
    validate: (target)=>isValidDsl(target),
    toSample: (seedInput, target)=>({
            messages: [
                {
                    role: 'system',
                    content: DSL_SYSTEM
                },
                {
                    role: 'user',
                    content: seedInput
                },
                {
                    role: 'assistant',
                    content: target
                }
            ]
        })
};
const EXPRESSION_FORMS = new Set([
    'expression_function',
    'expression_syntax',
    'natural_language'
]);
function dslCorpusToSamples(items) {
    const samples = [];
    for (const it of items){
        const form = Form.safeParse(it.meta.form);
        const dsl = form.success && EXPRESSION_FORMS.has(form.data) ? it.meta.expression ?? null : null;
        if (dsl && crownDslNano.validate(dsl)) samples.push(crownDslNano.toSample(it.text, dsl));
    }
    return samples;
}
export { WRONG_RESULT, crownDslNano, diagnosticReasons, dslCorpusToSamples, dslDiagnostics, dslReasons, extractDsl, feedbackTurn, isValidDsl, parseRuleTarget, wrapDslAsRule };
